import { Component, Injector, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd } from '@angular/router';
import { AbstractComponent, AuthenticationService, Company, PlansModalBridgeService, SocketService, UserService, ZAPICallManagerService } from 'lib-trend-core';
import { catchError, concatMap, filter, interval, map, of, Subject, switchMap, takeUntil } from 'rxjs';
import { Constants } from '../../../../environments/constantes';
import { PlansModalComponent } from '../plans-modal/plans-modal.component';
import { shouldHideFooter } from '../footer.util';

export type CompanyPlanType = 'trial' | 'starter' | 'essential' | 'pro' | 'enterprise';

@Component({
    selector: 'base-root',
    // templateUrl: `./base.component.html`,
    templateUrl: `./base.v2.component.html`,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BaseComponent extends AbstractComponent implements OnInit, OnDestroy {

  sidenav!: MouseEvent;

  trialDaysRemaining: number = 0;
  trialExpired: boolean = false;

  hideFooter: boolean = false;

  constructor(
    injector: Injector,
    private socketService: SocketService,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private dialog: MatDialog,
    private plansModalBridge: PlansModalBridgeService,
    private zapiCallManager: ZAPICallManagerService
  ) {
    super(injector);
    this.socketService.connect();
    this.configSocket();
    this.startTokenRefresh();
    this.startSessionHeartbeat();
  }

  ngOnInit(): void {
    if (this.getCurrentUser()) this.zapiCallManager.start();
    this.updateUserSession();
    this.fetchMaxUsersAndTotalUsersCompanyUserLogged();
    this.calculateTrialDays();
    this.setupFooterVisibility();

    this.plansModalBridge.open$
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload) => {
        const alreadyOpen = this.dialog.openDialogs.some(d => d.componentInstance instanceof PlansModalComponent);
        if (alreadyOpen) return;

        this.openPlansModal();
      });
  }

  override ngOnDestroy(): void {
    this.zapiCallManager.stop();
    super.ngOnDestroy();
    this.socketService.disconnect();
  }

  private calculateTrialDays(): void {
    const company: Company = this.getCompanyCurrentUser();
    if (company?.trial?.activated && company.trial.startDate) {
      const startDate = new Date(company.trial.startDate);
      const expirationDate = new Date(startDate);
      expirationDate.setDate(expirationDate.getDate() + company.trial.durationDays);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      expirationDate.setHours(0, 0, 0, 0);

      const diffTime = expirationDate.getTime() - today.getTime();
      const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      this.trialDaysRemaining = Math.max(0, daysRemaining);
      this.trialExpired = daysRemaining < 0;
    }
  }

  private setupFooterVisibility(): void {
    this.updateHideFooter();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.updateHideFooter());
  }

  private updateHideFooter(): void {
    this.hideFooter = shouldHideFooter(this.router.routerState.snapshot.root);
  }

  public startTokenRefresh() {
    interval(5 * 60 * 1000)
      .pipe(
        takeUntil(this.destroy$),
        map(() => this.updateUserSession())
      )
      .subscribe({
        next: () => console.log('The user session was updated!'),
        error: (err) => console.error('Error updating user session', err)
      });
  }

  /**
   * Keeps the online marker the attendance distribution reads ("Atendentes Online") alive. It has to
   * beat well inside the cache TTL (5 min): at the same 5 min as startTokenRefresh the key expired
   * exactly when the renewal was due, so a working agent read as offline whenever the request was
   * late or the browser throttled the timer in a background tab. It is a separate (204, one Redis
   * command) call precisely so beating this often costs nothing.
   */
  private startSessionHeartbeat(): void {
    interval(2 * 60 * 1000)
      .pipe(
        // catchError inside the switchMap: a failed beat must not tear down the interval.
        switchMap(() => this.authenticationService.heartbeatSession().pipe(catchError(() => of(null)))),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private fetchMaxUsersAndTotalUsersCompanyUserLogged(): void {
    const company: Company = this.getCompanyCurrentUser();

    this.userService.getCountTotalUsers(company._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value) => {
          localStorage.setItem(Constants.currentMaxUsersCompany, company?.maxUsers.toString());
          localStorage.setItem(Constants.currentTotalUsers, value.toString());
        },
        error: () => { }
      });
  }

  public updateUserSession() {
    this.authenticationService.refreshJwtTokenUserLogged()
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  isExpanded = true;
  menuItems = [
    { icon: 'home', text: 'Home' },
    { icon: 'account_circle', text: 'Profile' },
    { icon: 'settings', text: 'Settings' },
  ];

  public toggleSidenav() {
    this.isExpanded = !this.isExpanded;
  }

  public openPlansModal(): void {
    this.dialog.open(PlansModalComponent, {
      width: '90vw',
      height: '95vh',
      panelClass: 'plans-modal-container'
    });
  }

  /*********************************************************************************
   ***************************** Socket Methods - init *****************************
   *********************************************************************************/

  private updateSessionUser$ = new Subject<boolean>();

  private setupUpdateSessionUser() {
    this.updateSessionUser$.pipe(
      concatMap((update: boolean) => {
        if (update) {
          this.updateUserSession();
          this.fetchMaxUsersAndTotalUsersCompanyUserLogged();
        }
        return of(null);
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  public configSocket() {
    const code = super.getCodeCompanyCurrentUser();
    const idCurrentUser = super.getIDCurrentUser();

    this.setupUpdateSessionUser();

    const snameEventUser = `event_user_${code}_${idCurrentUser}`;
    this.socketService.joinEvent(snameEventUser);
    this.socketService.listen(snameEventUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateSessionUser$.next(true));
  }

  /*********************************************************************************
   ***************************** Socket Methods - end  *****************************
   *********************************************************************************/

}
